import { Collection, Db } from 'mongodb';
import { BoundedContext } from '../../../strategic-design/domain/entities/BoundedContext';
import type { IBoundedContextRepository } from '../../domain/ports/IBoundedContextRepository';
import { DDD_COLLECTIONS, DEFAULT_DDD_PROJECT_ID } from '../../../../config/collections';

export class MongoBoundedContextRepository implements IBoundedContextRepository {
  private collection?: Collection;

  constructor(
    private readonly database: Db
  ) {
    // DO NOT initialize collection in constructor!
    // MongoDB might not be connected yet during DI container setup
  }

  private async getCollection(): Promise<Collection> {
    // Lazy initialization - only get collection when actually needed
    if (!this.collection) {
      this.collection = this.database.collection(DDD_COLLECTIONS.BOUNDED_CONTEXTS);
    }
    return this.collection;
  }


  async save(boundedContext: BoundedContext): Promise<void> {
    const collection = await this.getCollection();
    const doc = boundedContext.toJSON();
    await collection.replaceOne(
      { id: boundedContext.id },
      doc,
      { upsert: true }
    );
  }

  async findById(id: string): Promise<BoundedContext | null> {
    const collection = await this.getCollection();
    const doc = await collection.findOne({ id });
    if (!doc) return null;
    
    return BoundedContext.reconstitute(
      {
        domainId: doc.domainId,
        name: doc.name,
        description: doc.description,
        type: doc.type,
        ubiquitousLanguage: doc.ubiquitousLanguage || {},
        domainServices: doc.domainServices || [],
        aggregates: doc.aggregates || [],
        valueObjects: doc.valueObjects || [],
        entities: doc.entities || []
      },
      doc.id,
      new Date(doc.createdAt),
      new Date(doc.updatedAt)
    );
  }

  async findByName(domainId: string, name: string): Promise<BoundedContext | null> {
    const collection = await this.getCollection();
    const doc = await collection.findOne({ domainId, name });
    if (!doc) return null;
    
    return BoundedContext.reconstitute(
      {
        domainId: doc.domainId,
        name: doc.name,
        description: doc.description,
        type: doc.type,
        ubiquitousLanguage: doc.ubiquitousLanguage || {},
        domainServices: doc.domainServices || [],
        aggregates: doc.aggregates || [],
        valueObjects: doc.valueObjects || [],
        entities: doc.entities || []
      },
      doc.id,
      new Date(doc.createdAt),
      new Date(doc.updatedAt)
    );
  }

  async findByDomainId(domainId: string, options?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    type?: 'core' | 'supporting' | 'generic';
  }): Promise<{ contexts: BoundedContext[]; totalCount: number }> {
    const collection = await this.getCollection();
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 100;
    const skip = (page - 1) * pageSize;

    const query: any = { domainId };
    
    // Add projectId filter - include default project or null/missing values
    query.$and = [
      {
        $or: [
          { projectId: DEFAULT_DDD_PROJECT_ID },
          { projectId: { $exists: false } },
          { projectId: null }
        ]
      }
    ];
    
    if (options?.searchTerm) {
      query.$and.push({
        $or: [
          { name: { $regex: options.searchTerm, $options: 'i' } },
          { description: { $regex: options.searchTerm, $options: 'i' } }
        ]
      });
    }
    
    if (options?.type) {
      query.type = options.type;
    }

    const [docs, totalCount] = await Promise.all([
      collection
        .find(query)
        .skip(skip)
        .limit(pageSize)
        .toArray(),
      collection.countDocuments(query)
    ]);

    const contexts = docs.map(doc => 
      BoundedContext.reconstitute(
        {
          domainId: doc.domainId,
          name: doc.name,
          description: doc.description,
          type: doc.type,
          ubiquitousLanguage: doc.ubiquitousLanguage || {},
          domainServices: doc.domainServices || [],
          aggregates: doc.aggregates || [],
          valueObjects: doc.valueObjects || [],
          entities: doc.entities || []
        },
        doc.id,
        new Date(doc.createdAt),
        new Date(doc.updatedAt)
      )
    );

    return { contexts, totalCount };
  }

  async findAll(options?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    domainId?: string;
    type?: 'core' | 'supporting' | 'generic';
  }): Promise<{ contexts: BoundedContext[]; totalCount: number }> {
    const collection = await this.getCollection();
    const page = options?.page || 1;
    const pageSize = options?.pageSize || 100;
    const skip = (page - 1) * pageSize;

    const query: any = {};
    
    // Add projectId filter - include default project or null/missing values
    const conditions: any[] = [
      {
        $or: [
          { projectId: DEFAULT_DDD_PROJECT_ID },
          { projectId: { $exists: false } },
          { projectId: null }
        ]
      }
    ];
    
    if (options?.domainId) {
      query.domainId = options.domainId;
    }
    
    if (options?.searchTerm) {
      conditions.push({
        $or: [
          { name: { $regex: options.searchTerm, $options: 'i' } },
          { description: { $regex: options.searchTerm, $options: 'i' } }
        ]
      });
    }
    
    if (conditions.length > 0) {
      query.$and = conditions;
    }
    
    if (options?.type) {
      query.type = options.type;
    }

    const [docs, totalCount] = await Promise.all([
      collection
        .find(query)
        .skip(skip)
        .limit(pageSize)
        .toArray(),
      collection.countDocuments(query)
    ]);

    const contexts = docs.map(doc => 
      BoundedContext.reconstitute(
        {
          domainId: doc.domainId,
          name: doc.name,
          description: doc.description,
          type: doc.type,
          ubiquitousLanguage: doc.ubiquitousLanguage || {},
          domainServices: doc.domainServices || [],
          aggregates: doc.aggregates || [],
          valueObjects: doc.valueObjects || [],
          entities: doc.entities || []
        },
        doc.id,
        new Date(doc.createdAt),
        new Date(doc.updatedAt)
      )
    );

    return { contexts, totalCount };
  }

  async deleteById(id: string): Promise<void> {
    const collection = await this.getCollection();
    await collection.deleteOne({ id });
  }

  async existsByName(domainId: string, name: string): Promise<boolean> {
    const collection = await this.getCollection();
    const count = await collection.countDocuments({ domainId, name });
    return count > 0;
  }

  async findByBoundedContextId(boundedContextId: string): Promise<BoundedContext | null> {
    return this.findById(boundedContextId);
  }
}